import type { GuideTourStep } from '../engine/types';

/** Each lookup walks the whole document, so repeated ones are spaced out. */
export const ANCHOR_LOOKUP_INTERVAL_MS = 200;

export function findAnchor(topicId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-guide-id="${topicId}"]`);
}

/**
 * Indices of the steps the manager gets to see: those whose anchor is in the
 * DOM, plus `index` itself even if its anchor just left. The tour's first
 * step, the next and previous buttons and the "Paso X de Y" counter all read
 * this one list, so a step that never rendered is skipped and left out of the
 * count alike — counting it read "Paso 2 de 3" on a tour that ends with
 * "Listo" at 2. Pass -1 when no step is showing yet.
 */
export function visibleStepIndices(
  steps: readonly GuideTourStep[],
  index: number,
): number[] {
  return steps.flatMap((step, i) =>
    i === index || findAnchor(step.topicId) ? [i] : [],
  );
}

/**
 * Resolves with the first step that has an anchor in the DOM, polling until
 * one shows up or the budget runs out (-1).
 *
 * A screen can paint its shell before the data its anchors live in arrives, so
 * looking only on the first frame would find nothing and burn the tour. The
 * budget is wall-clock rather than a frame count: frames are the wrong unit
 * here — they halve on a 120Hz monitor and stop entirely in a background tab.
 *
 * Returns a canceller so a screen that unmounts mid-poll stops the loop.
 */
export function waitForFirstVisibleStep(
  steps: readonly GuideTourStep[],
  onResolved: (index: number) => void,
  timeoutMs = 1000,
): () => void {
  const deadline = performance.now() + timeoutMs;
  let frame = 0;

  const tick = () => {
    const index = visibleStepIndices(steps, -1)[0] ?? -1;
    if (index !== -1 || performance.now() > deadline) {
      onResolved(index);
      return;
    }
    frame = requestAnimationFrame(tick);
  };
  tick();

  return () => cancelAnimationFrame(frame);
}
